import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useMarketingCopy } from '../context/MarketingCopyContext';
import { localizedPath } from '../utils/routes';
import PromotionBanner from '../components/PromotionBanner';
import SEOHead from '../components/SEOHead';
import SlotPicker from '../components/SlotPicker';
import VehicleSelector from '../components/VehicleSelector';

const API = process.env.REACT_APP_API_URL || '/api';

export default function BookAppointment() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const { mc } = useMarketingCopy();
  const navigate = useNavigate();
  const { lang } = useParams();
  const currentLang = lang || language;
  const lp = (routeName) => localizedPath(routeName, currentLang);
  const [searchParams] = useSearchParams();
  const [step, setStepRaw] = useState(1);
  const setStep = (s) => { setStepRaw(s); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const [services, setServices] = useState([]);
  const [savedVehicles, setSavedVehicles] = useState([]);
  const [useExisting, setUseExisting] = useState(null);

  const [form, setForm] = useState({
    service_ids: [], appointment_date: '', appointment_time: '', notes: '',
    vehicle_id: '',
    vehicle: { make: '', model: '', year: new Date().getFullYear(), license_plate: '' }
  });

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Business hours (for disabling closed days)
  const [businessHours, setBusinessHours] = useState([]);

  const initialServiceId = searchParams.get('serviceId');

  useEffect(() => {
    axios.get(`${API}/services?lang=${language}`).then(r => {
      setServices(r.data);
      // Auto-select service from query param
      if (initialServiceId) {
        const id = parseInt(initialServiceId);
        if (r.data.some(s => s.id === id)) {
          setForm(prev => ({ ...prev, service_ids: [id] }));
        }
      }
    }).catch(() => {});
    axios.get(`${API}/vehicles`).then(r => setSavedVehicles(r.data)).catch(() => {});
    axios.get(`${API}/schedule/hours`).then(r => setBusinessHours(r.data)).catch(() => {});
  }, [initialServiceId, language]);

  // Computed values for selected services
  const selectedServices = services.filter(s => form.service_ids.includes(s.id));
  const totalDuration = selectedServices.reduce((sum, s) => sum + (s.duration_minutes || 60), 0);
  const totalPriceMin = selectedServices.reduce((sum, s) => sum + parseFloat(s.price_min || 0), 0);
  const totalPriceMax = selectedServices.reduce((sum, s) => sum + parseFloat(s.price_max || 0), 0);
  const totalDeposit = selectedServices.reduce((sum, s) => sum + (parseFloat(s.deposit_amount) || 0), 0);
  const selectedVehicle = savedVehicles.find(v => v.id === form.vehicle_id);

  const getMinDate = () => {
    const d = new Date();
    return d.toISOString().split('T')[0];
  };

  const isClosedDay = (dateStr) => {
    if (!dateStr || businessHours.length === 0) return false;
    const date = new Date(dateStr + 'T12:00:00');
    const dayOfWeek = date.getDay();
    const dayHours = businessHours.find(h => h.day_of_week === dayOfWeek);
    return dayHours?.is_closed ?? false;
  };

  const handleSubmit = async () => {
    setError(''); setLoading(true);
    try {
      const payload = {
        service_ids: form.service_ids,
        appointment_date: form.appointment_date,
        appointment_time: form.appointment_time,
        notes: form.notes
      };

      if (useExisting) {
        payload.vehicle_id = form.vehicle_id;
      } else {
        payload.vehicle = form.vehicle;
      }

      const response = await axios.post(`${API}/appointments`, payload);

      // Check if payment is required (Stripe deposit)
      if (response.data.payment_required) {
        setLoading(false);
        setPaymentLoading(true);
        try {
          const payRes = await axios.post(`${API}/payments/create-session`, {
            appointment_id: response.data.id,
            lang: currentLang
          });
          // Redirect to Stripe Checkout
          window.location.href = payRes.data.checkout_url;
        } catch (payErr) {
          setPaymentLoading(false);
          setError(payErr.response?.data?.error || 'Payment session creation failed. Your appointment has been created — please contact us to complete payment.');
        }
      } else {
        setSuccess(true);
      }
    } catch (err) {
      setError(err.response?.data?.error || t.booking.bookingError);
    } finally { setLoading(false); }
  };

  if (success) return (
    <div style={{ background: '#0C1220', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
      <div style={{ textAlign: 'center', maxWidth: '500px' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1.5rem' }}>✓</div>
        <div style={{ color: '#D42B2B', fontSize: '0.6rem', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '1rem' }}>{t.booking.success.confirmed}</div>
        <h2 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '2.5rem', color: '#E8ECF0', fontWeight: 300, marginBottom: '1rem' }}>{t.booking.success.title}</h2>
        <p style={{ color: '#64748B', fontSize: '0.9rem', lineHeight: 1.8, marginBottom: '2rem' }}>
          {mc('book', 'success.message', t.booking.success.message)}
        </p>
        <button onClick={() => navigate(lp('dashboard'))} className="btn btn-primary" style={{ padding: '1rem 2.5rem' }}>
          {t.booking.success.viewButton}
        </button>
      </div>
    </div>
  );

  const labelStyle = { display: 'block', fontSize: '0.65rem', letterSpacing: '0.1em', color: '#7C8CA0', textTransform: 'uppercase', marginBottom: '0.6rem', fontFamily: "'Barlow', sans-serif", fontWeight: 500 };

  return (
    <div style={{ background: '#0C1220', minHeight: '100vh', paddingTop: 'var(--cjpa-nav-offset)' }}>
      <SEOHead page="book" />
      <div style={{ maxWidth: '960px', margin: '0 auto', padding: 'clamp(2rem, 4vw, 4rem) clamp(1rem, 3vw, 2rem)', display: 'flex', gap: '2rem', alignItems: 'flex-start' }}>
      <div style={{ flex: 1, maxWidth: '760px' }}>
        <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <div style={{ fontSize: '0.6rem', letterSpacing: '0.1em', color: '#7C8CA0', textTransform: 'uppercase', marginBottom: '1rem' }}>{mc('book', 'subtitle', t.booking.subtitle)}</div>
          <h1 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 'clamp(2.5rem, 5vw, 4rem)', color: '#E8ECF0', fontWeight: 300 }}>
            {t.booking.title} <span style={{ color: '#D42B2B', fontStyle: 'italic' }}>{t.booking.titleHighlight}</span>
          </h1>
        </div>

        {/* Step indicators */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '0', marginBottom: '3rem', position: 'relative' }}>
          {[t.booking.steps.service, t.booking.steps.schedule, t.booking.steps.vehicle, t.booking.steps.review].map((s, i) => (
            <div key={s} style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.4rem' }}>
                <div style={{
                  width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: step > i+1 ? '#D42B2B' : step === i+1 ? 'transparent' : 'transparent',
                  border: step >= i+1 ? '1px solid #D42B2B' : '1px solid rgba(148,163,184,0.2)',
                  color: step >= i+1 ? '#D42B2B' : '#64748B', fontSize: '0.7rem', fontWeight: 600,
                }}>{step > i+1 ? '✓' : i+1}</div>
                <div className="cjpa-step-label" style={{ fontSize: '0.55rem', letterSpacing: '0.15em', color: step >= i+1 ? '#D42B2B' : '#64748B', textTransform: 'uppercase' }}>{s}</div>
              </div>
              {i < 3 && <div className="cjpa-stepper-line" style={{ width: '60px', height: '1px', background: step > i+1 ? '#D42B2B' : 'rgba(148,163,184,0.15)', margin: '0 0.5rem', marginBottom: '1.5rem' }} />}
            </div>
          ))}
        </div>

        <div style={{ background: '#111827', border: '1px solid rgba(148,163,184,0.15)', padding: 'clamp(1.5rem, 3vw, 2.5rem)' }}>
          {error && <div className="alert alert-danger">{error}</div>}

          {/* Step 1: Service (multi-select) */}
          {step === 1 && (
            <div>
              <h3 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '1.8rem', color: '#E8ECF0', fontWeight: 300, marginBottom: '0.5rem' }}>{t.booking.selectService}</h3>
              <p style={{ color: '#7C8CA0', fontSize: '0.8rem', marginBottom: '1.5rem' }}>
                {t.booking.selectMultipleHint || 'Select one or more services for your appointment'}
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1px', background: 'rgba(148,163,184,0.1)', marginBottom: '1.5rem' }}>
                {services.map(s => {
                  const isSelected = form.service_ids.includes(s.id);
                  const atMax = form.service_ids.length >= 6 && !isSelected;
                  return (
                    <div key={s.id} onClick={() => {
                      if (atMax) return;
                      setForm(prev => ({
                        ...prev,
                        service_ids: isSelected
                          ? prev.service_ids.filter(id => id !== s.id)
                          : [...prev.service_ids, s.id]
                      }));
                    }}
                      style={{
                        background: isSelected ? 'rgba(212,43,43,0.08)' : '#111827',
                        border: isSelected ? '1px solid #D42B2B' : '1px solid transparent',
                        padding: '1.8rem', cursor: atMax ? 'not-allowed' : 'pointer', transition: 'all 0.2s',
                        opacity: atMax ? 0.5 : 1, position: 'relative',
                      }}>
                      <div style={{ position: 'absolute', top: '0.8rem', right: '0.8rem', width: '20px', height: '20px', borderRadius: '4px',
                        border: isSelected ? '2px solid #D42B2B' : '2px solid rgba(148,163,184,0.3)',
                        background: isSelected ? '#D42B2B' : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#0C1220', fontSize: '0.7rem', fontWeight: 700
                      }}>{isSelected ? '✓' : ''}</div>
                      <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '1.15rem', color: isSelected ? '#D42B2B' : '#E8ECF0', marginBottom: '0.5rem', paddingRight: '1.5rem' }}>{s.name}</div>
                      <div style={{ color: '#94A3B8', fontSize: '0.78rem', lineHeight: 1.6, marginBottom: '0.7rem' }}>{s.description?.substring(0, 75)}...</div>
                      <div style={{ color: '#D42B2B', fontSize: '0.85rem' }}>{t.services.from} ${s.price_min} · {s.duration_minutes}min</div>
                    </div>
                  );
                })}
              </div>
              {/* Selection summary bar */}
              {form.service_ids.length > 0 && (
                <div style={{ background: 'rgba(212,43,43,0.06)', border: '1px solid rgba(212,43,43,0.3)', padding: '1rem 1.5rem', marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <div>
                    <span style={{ color: '#D42B2B', fontWeight: 600, fontSize: '0.9rem' }}>{form.service_ids.length}</span>
                    <span style={{ color: '#94A3B8', fontSize: '0.85rem', marginLeft: '0.4rem' }}>{t.booking.servicesSelected || 'service(s) selected'}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '1.5rem', color: '#94A3B8', fontSize: '0.8rem' }}>
                    <span>{t.booking.totalDuration || 'Duration'}: <strong style={{ color: '#E8ECF0' }}>{totalDuration}min</strong></span>
                    <span>{t.booking.totalPrice || 'Price'}: <strong style={{ color: '#E8ECF0' }}>${totalPriceMin.toFixed(2)}–${totalPriceMax.toFixed(2)}</strong></span>
                  </div>
                </div>
              )}
              {form.service_ids.length >= 6 && (
                <div style={{ color: '#D42B2B', fontSize: '0.8rem', marginBottom: '1rem' }}>{t.booking.maxServices || 'Maximum 6 services per appointment'}</div>
              )}
              <button disabled={form.service_ids.length === 0} onClick={() => setStep(2)} className="btn btn-primary" style={{ padding: '1rem 2.2rem' }}>
                {t.booking.continueButton || 'Continue'}
              </button>
            </div>
          )}

          {/* Step 2: Date & Time */}
          {step === 2 && (
            <div>
              <h3 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '1.8rem', color: '#E8ECF0', fontWeight: 300, marginBottom: '1.5rem' }}>{t.booking.chooseDatetime}</h3>
              <SlotPicker
                serviceIds={form.service_ids}
                selectedDate={form.appointment_date}
                selectedTime={form.appointment_time}
                onDateChange={val => {
                  setForm({...form, appointment_date: val, appointment_time: ''});
                  setError('');
                }}
                onTimeChange={time => setForm({...form, appointment_time: time})}
                language={language}
                t={t}
              />
              <div className="mb-4">
                <label className="form-label">{t.booking.notesLabel}</label>
                <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}
                  placeholder={t.booking.notesPlaceholder} rows={3}
                  className="form-control"
                />
              </div>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <button onClick={() => setStep(1)} className="btn btn-outline-primary" style={{ padding: '1rem 1.8rem' }}>{t.booking.backButton}</button>
                <button disabled={!form.appointment_date || !form.appointment_time} onClick={() => setStep(3)} className="btn btn-primary" style={{ padding: '1rem 2.2rem' }}>
                  {t.booking.continueButton}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Vehicle */}
          {step === 3 && (
            <div>
              <h3 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '1.8rem', color: '#E8ECF0', fontWeight: 300, marginBottom: '1.5rem' }}>{t.booking.yourVehicle}</h3>

              {savedVehicles.length > 0 && useExisting === null && (
                <div className="row mb-4">
                  <div className="col-12 col-md-6">
                    <button onClick={() => setUseExisting(true)} style={{ width: '100%', background: '#1a2332', border: '1px solid rgba(148,163,184,0.3)', color: '#D42B2B', padding: '1.5rem', fontFamily: "'Barlow', sans-serif", fontSize: '0.7rem', letterSpacing: '0.15em', textTransform: 'uppercase', cursor: 'pointer', fontWeight: 500 }}>
                      {t.booking.useSaved}
                    </button>
                  </div>
                  <div className="col-12 col-md-6">
                    <button onClick={() => setUseExisting(false)} style={{ width: '100%', background: '#1a2332', border: '1px solid rgba(148,163,184,0.3)', color: '#D42B2B', padding: '1.5rem', fontFamily: "'Barlow', sans-serif", fontSize: '0.7rem', letterSpacing: '0.15em', textTransform: 'uppercase', cursor: 'pointer', fontWeight: 500 }}>
                      {t.booking.addNew}
                    </button>
                  </div>
                </div>
              )}

              {(savedVehicles.length === 0 || useExisting === false) && (
                <VehicleSelector
                  value={form.vehicle}
                  onChange={vehicle => setForm({...form, vehicle})}
                  t={t}
                />
              )}

              {useExisting === true && (
                <div className="mb-4">
                  <label style={labelStyle}>{t.booking.selectVehicle}</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
                    {savedVehicles.map(v => (
                      <div key={v.id} onClick={() => setForm({...form, vehicle_id: v.id})}
                        style={{
                          background: form.vehicle_id === v.id ? 'rgba(148,163,184,0.12)' : '#1a2332',
                          border: form.vehicle_id === v.id ? '1px solid rgba(148,163,184,0.6)' : '1px solid rgba(148,163,184,0.2)',
                          padding: '1.2rem', cursor: 'pointer', transition: 'all 0.2s',
                        }}>
                        <div style={{ color: form.vehicle_id === v.id ? '#D42B2B' : '#E8ECF0', fontFamily: "'Rajdhani', sans-serif", fontSize: '1.1rem' }}>
                          {v.year} {v.make} {v.model}
                        </div>
                        {v.license_plate && <div style={{ color: '#94A3B8', fontSize: '0.75rem', marginTop: '0.3rem' }}>{v.license_plate}</div>}
                      </div>
                    ))}
                  </div>
                  <button onClick={() => setUseExisting(false)} className="btn btn-outline-primary btn-sm mt-2">
                    {t.booking.addDifferent}
                  </button>
                </div>
              )}

              <div style={{ display: 'flex', gap: '1rem' }}>
                <button onClick={() => { setStep(2); setUseExisting(null); }} className="btn btn-outline-primary" style={{ padding: '1rem 1.8rem' }}>{t.booking.backButton}</button>
                <button
                  disabled={(useExisting && !form.vehicle_id) || (!useExisting && (!form.vehicle.make || !form.vehicle.model))}
                  onClick={() => setStep(4)}
                  className="btn btn-primary"
                  style={{ padding: '1rem 2.2rem' }}
                >{t.booking.continueButton}</button>
              </div>
            </div>
          )}

          {/* Step 4: Review */}
          {step === 4 && (
            <div>
              <h3 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '1.8rem', color: '#E8ECF0', fontWeight: 300, marginBottom: '1.5rem' }}>{t.booking.reviewConfirm}</h3>
              {/* Services list */}
              <div style={{ padding: '1.3rem 0', borderBottom: '1px solid rgba(148,163,184,0.1)' }}>
                <div style={{ fontSize: '0.62rem', letterSpacing: '0.08em', color: '#7C8CA0', textTransform: 'uppercase', marginBottom: '0.7rem', fontWeight: 500 }}>{t.booking.reviewLabels.service}</div>
                {selectedServices.map(s => (
                  <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.4rem 0' }}>
                    <div style={{ color: '#E8ECF0', fontSize: '0.9rem', fontFamily: "'Rajdhani', sans-serif" }}>{s.name}</div>
                    <div style={{ color: '#94A3B8', fontSize: '0.8rem' }}>${s.price_min}–${s.price_max} · {s.duration_minutes}min</div>
                  </div>
                ))}
                {selectedServices.length > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.6rem 0 0', borderTop: '1px solid rgba(148,163,184,0.08)', marginTop: '0.5rem' }}>
                    <div style={{ color: '#D42B2B', fontSize: '0.85rem', fontWeight: 600 }}>{t.booking.totalLabel || 'Total'}</div>
                    <div style={{ color: '#D42B2B', fontSize: '0.85rem', fontWeight: 600 }}>${totalPriceMin.toFixed(2)}–${totalPriceMax.toFixed(2)} · {totalDuration}min</div>
                  </div>
                )}
              </div>
              {/* Other review rows */}
              {[
                [t.booking.reviewLabels.datetime, `${form.appointment_date} ${t.booking.at} ${form.appointment_time}`, `~${totalDuration} ${t.services.minutes}`],
                [t.booking.reviewLabels.vehicle, useExisting && selectedVehicle ? `${selectedVehicle.year} ${selectedVehicle.make} ${selectedVehicle.model}` : `${form.vehicle.year} ${form.vehicle.make} ${form.vehicle.model}`, (useExisting && selectedVehicle?.license_plate) || form.vehicle.license_plate || ''],
                [t.booking.reviewLabels.client, `${user?.first_name} ${user?.last_name}`, user?.email],
              ].map(([label, main, sub]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.3rem 0', borderBottom: '1px solid rgba(148,163,184,0.1)' }}>
                  <div>
                    <div style={{ fontSize: '0.62rem', letterSpacing: '0.08em', color: '#7C8CA0', textTransform: 'uppercase', marginBottom: '0.4rem', fontWeight: 500 }}>{label}</div>
                    <div style={{ color: '#E8ECF0', fontSize: '0.95rem', fontFamily: "'Rajdhani', sans-serif" }}>{main}</div>
                  </div>
                  <div style={{ color: '#94A3B8', fontSize: '0.8rem', textAlign: 'right' }}>{sub}</div>
                </div>
              ))}
              {totalDeposit > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.3rem 0', borderBottom: '1px solid rgba(148,163,184,0.1)' }}>
                  <div>
                    <div style={{ fontSize: '0.62rem', letterSpacing: '0.08em', color: '#7C8CA0', textTransform: 'uppercase', marginBottom: '0.4rem', fontWeight: 500 }}>{t.booking.depositAmount}</div>
                    <div style={{ color: '#D42B2B', fontSize: '0.95rem', fontFamily: "'Rajdhani', sans-serif", fontWeight: 600 }}>${totalDeposit.toFixed(2)} CAD</div>
                  </div>
                  <div style={{ color: '#94A3B8', fontSize: '0.8rem', textAlign: 'right' }}>{t.booking.depositRequired}</div>
                </div>
              )}
              {form.notes && <div style={{ marginTop: '1.2rem', padding: '1.1rem', background: 'rgba(148,163,184,0.04)', border: '1px solid rgba(148,163,184,0.1)' }}>
                <div style={{ fontSize: '0.62rem', letterSpacing: '0.08em', color: '#7C8CA0', textTransform: 'uppercase', marginBottom: '0.5rem', fontWeight: 500 }}>{t.booking.reviewLabels.notes}</div>
                <div style={{ color: '#64748B', fontSize: '0.82rem', lineHeight: 1.6 }}>{form.notes}</div>
              </div>}
              <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
                <button onClick={() => setStep(3)} className="btn btn-outline-primary" style={{ padding: '1rem 1.8rem' }}>{t.booking.backButton}</button>
                <button onClick={handleSubmit} disabled={loading || paymentLoading} className="btn btn-primary" style={{ flex: 1, padding: '1rem 2.2rem' }}>
                  {loading ? t.booking.bookingButton : paymentLoading ? t.booking.paymentProcessing : totalDeposit > 0 ? t.booking.proceedToPayment : t.booking.confirmButton}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="d-none d-lg-block" style={{ width: '220px', flexShrink: 0 }}>
        <PromotionBanner placement="booking_sidebar" />
        <div style={{ marginTop: '1.5rem', overflow: 'hidden' }}>
          <img
            src="/images/about/workshop-interior.jpg"
            alt={t.images?.aboutShop || ''}
            width={220}
            height={160}
            loading="lazy"
            style={{ width: '100%', filter: 'brightness(0.6)', border: '1px solid rgba(148,163,184,0.15)', display: 'block' }}
            onError={e => { e.target.parentElement.style.display = 'none'; }}
          />
        </div>
      </div>
      </div>
    </div>
  );
}
